import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  limit,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { tripPlanModel, createTripPlan, validateTripPlan, formatTripPlanForDisplay } from '../data/tripPlanModel';

// CRUD Operations for Trip Plans

// Create a new trip plan
export const createTripPlanDocument = async (userId, userName, userRole, tripData) => {
  try {
    console.log('🏗️ Creating trip plan document');
    console.log('👤 User info:', { userId, userName, userRole });
    console.log('📝 Trip data:', tripData);
    
    const tripPlan = createTripPlan(userId, userName, userRole, tripData);
    console.log('📋 Created trip plan object:', tripPlan);
    
    const validation = validateTripPlan(tripPlan);
    console.log('✅ Validation result:', validation);
    
    if (!validation.isValid) {
      console.log('❌ Validation failed:', validation.errors);
      throw new Error(`Validation failed: ${Object.values(validation.errors).join(', ')}`);
    }
    
    // Add check-in configuration from form data
    if (tripData.checkinOrganizers !== undefined || tripData.checkinParticipants !== undefined || tripData.checkinGirls !== undefined) {
      console.log('🔧 Adding check-in configuration');
      tripPlan.checkinConfig = {
        enabled: {
          organizers: tripData.checkinOrganizers || false,
          participants: tripData.checkinParticipants || false,
          girls: tripData.checkinGirls || false
        },
        settings: {
          allowManualCheckin: tripData.allowManualCheckin !== false,
          allowQRCheckin: tripData.allowQRCheckin !== false,
          requireLocation: false,
          checkinRadius: 100
        },
        status: {
          organizersCheckedIn: [],
          participantsCheckedIn: [],
          girlsCheckedIn: [],
          lastCheckinTime: null,
          totalCheckins: 0
        }
      };
    }
    
    console.log('💾 Saving to Firestore...');
    const tripPlansRef = collection(db, tripPlanModel.collection);
    const docRef = await addDoc(tripPlansRef, tripPlan);
    console.log('📄 Document created with ID:', docRef.id);
    
    // Update the document with its own ID
    await updateDoc(docRef, {
      id: docRef.id,
      lastUpdated: new Date()
    });
    console.log('🔄 Document updated with ID');
    
    console.log('✅ Trip plan created successfully');
    
    return {
      success: true,
      tripPlanId: docRef.id,
      data: { ...tripPlan, id: docRef.id }
    };
  } catch (error) {
    console.error('❌ Error creating trip plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get trip plan by ID
export const getTripPlanById = async (tripPlanId) => {
  try {
    if (!tripPlanId) {
      throw new Error('Trip plan ID is required');
    }
    
    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    const tripPlanSnap = await getDoc(tripPlanRef);
    
    if (tripPlanSnap.exists()) {
      return {
        success: true,
        data: formatTripPlanForDisplay({
          id: tripPlanSnap.id,
          ...tripPlanSnap.data()
        })
      };
    } else {
      return {
        success: false,
        error: 'Trip plan not found'
      };
    }
  } catch (error) {
    console.error('Error getting trip plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get trip plans by user ID
export const getTripPlansByUser = async (userId, limitCount = 50) => {
  try {
    const tripPlansRef = collection(db, tripPlanModel.collection);
    
    // Get all trip plans (no filter) to include both owned and helper trips
    const querySnapshot = await getDocs(tripPlansRef);
    const allPlans = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Filter out dummy data or invalid trip plans
      if (data.id && data.id !== '' && data.tripName && data.tripName.trim() !== '') {
        allPlans.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Filter to show only trips where user is owner or helper
    const userPlans = allPlans.filter(plan => {
      // User is owner
      if (plan.userId === userId) return true;
      
      // User is helper
      if (plan.helpers && Array.isArray(plan.helpers)) {
        return plan.helpers.some(helper => helper.userId === userId);
      }
      
      return false;
    });
    
    // Sort by createdAt in JavaScript instead of Firestore
    userPlans.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    // Apply limit
    const limitedPlans = userPlans.slice(0, limitCount);
    
    return {
      success: true,
      data: limitedPlans.map(plan => formatTripPlanForDisplay(plan))
    };
  } catch (error) {
    console.error('Error getting trip plans by user:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get public trip plans
export const getPublicTripPlans = async (limitCount = 20) => {
  try {
    const tripPlansRef = collection(db, tripPlanModel.collection);
    const q = query(
      tripPlansRef,
      where('isPublic', '==', true),
      where('status', '==', 'active'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const tripPlans = [];
    
    querySnapshot.forEach((doc) => {
      tripPlans.push(formatTripPlanForDisplay({
        id: doc.id,
        ...doc.data()
      }));
    });
    
    // Sort by createdAt in JavaScript instead of Firestore
    tripPlans.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return {
      success: true,
      data: tripPlans
    };
  } catch (error) {
    console.error('Error getting public trip plans:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Update trip plan
export const updateTripPlan = async (tripPlanId, updateData) => {
  try {
    if (!tripPlanId) {
      throw new Error('Trip plan ID is required');
    }
    
    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    const updatePayload = {
      ...updateData,
      updatedAt: new Date().toISOString()
    };
    
    // Handle check-in configuration updates
    if (updateData.checkinOrganizers !== undefined || updateData.checkinParticipants !== undefined || updateData.checkinGirls !== undefined) {
      // Get current trip plan to preserve existing checkinConfig
      const tripPlanSnap = await getDoc(tripPlanRef);
      const currentData = tripPlanSnap.data();
      
      const currentCheckinConfig = currentData.checkinConfig || {
        enabled: { organizers: false, participants: false, girls: false },
        settings: { allowManualCheckin: true, allowQRCheckin: true, requireLocation: false, checkinRadius: 100 },
        status: { organizersCheckedIn: [], participantsCheckedIn: [], girlsCheckedIn: [], lastCheckinTime: null, totalCheckins: 0 }
      };
      
      updatePayload.checkinConfig = {
        ...currentCheckinConfig,
        enabled: {
          organizers: updateData.checkinOrganizers !== undefined ? updateData.checkinOrganizers : currentCheckinConfig.enabled.organizers,
          participants: updateData.checkinParticipants !== undefined ? updateData.checkinParticipants : currentCheckinConfig.enabled.participants,
          girls: updateData.checkinGirls !== undefined ? updateData.checkinGirls : currentCheckinConfig.enabled.girls
        },
        settings: {
          allowManualCheckin: updateData.allowManualCheckin !== undefined ? updateData.allowManualCheckin : currentCheckinConfig.settings.allowManualCheckin,
          allowQRCheckin: updateData.allowQRCheckin !== undefined ? updateData.allowQRCheckin : currentCheckinConfig.settings.allowQRCheckin,
          requireLocation: currentCheckinConfig.settings.requireLocation,
          checkinRadius: currentCheckinConfig.settings.checkinRadius
        }
      };
    }
    
    await updateDoc(tripPlanRef, updatePayload);
    
    return {
      success: true,
      data: updatePayload
    };
  } catch (error) {
    console.error('Error updating trip plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Delete trip plan
export const deleteTripPlan = async (tripPlanId) => {
  try {
    if (!tripPlanId) {
      throw new Error('Trip plan ID is required');
    }
    
    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    await deleteDoc(tripPlanRef);
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error deleting trip plan:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Search trip plans
export const searchTripPlans = async (searchTerm, userId = null) => {
  try {
    const tripPlansRef = collection(db, tripPlanModel.collection);
    
    // Get all trip plans (no filter) to include both owned and helper trips
    const querySnapshot = await getDocs(tripPlansRef);
    const allPlans = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Filter out dummy data or invalid trip plans
      if (data.id && data.id !== '' && data.tripName && data.tripName.trim() !== '') {
        allPlans.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Filter to show only trips where user is owner or helper (if userId provided)
    let userPlans = allPlans;
    if (userId) {
      userPlans = allPlans.filter(plan => {
        // User is owner
        if (plan.userId === userId) return true;
        
        // User is helper
        if (plan.helpers && Array.isArray(plan.helpers)) {
          return plan.helpers.some(helper => helper.userId === userId);
        }
        
        return false;
      });
    }
    
    // Apply search filter
    const tripPlans = userPlans.filter(plan => {
      const searchableText = `${plan.tripName} ${plan.tripLocation} ${plan.tripTheme} ${plan.description}`.toLowerCase();
      return searchableText.includes(searchTerm.toLowerCase());
    });
    
    // Sort by createdAt in JavaScript instead of Firestore
    tripPlans.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return {
      success: true,
      data: tripPlans.map(plan => formatTripPlanForDisplay(plan))
    };
  } catch (error) {
    console.error('Error searching trip plans:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get trip plans by theme
export const getTripPlansByTheme = async (theme, userId = null) => {
  try {
    const tripPlansRef = collection(db, tripPlanModel.collection);
    
    // Get all trip plans (no filter) to include both owned and helper trips
    const querySnapshot = await getDocs(tripPlansRef);
    const allPlans = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      
      // Filter out dummy data or invalid trip plans
      if (data.id && data.id !== '' && data.tripName && data.tripName.trim() !== '') {
        allPlans.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Filter by theme first
    const themePlans = allPlans.filter(plan => plan.tripTheme === theme);
    
    // Filter to show only trips where user is owner or helper (if userId provided)
    let userPlans = themePlans;
    if (userId) {
      userPlans = themePlans.filter(plan => {
        // User is owner
        if (plan.userId === userId) return true;
        
        // User is helper
        if (plan.helpers && Array.isArray(plan.helpers)) {
          return plan.helpers.some(helper => helper.userId === userId);
        }
        
        return false;
      });
    }
    
    // Sort by createdAt in JavaScript instead of Firestore
    userPlans.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    
    return {
      success: true,
      data: userPlans.map(plan => formatTripPlanForDisplay(plan))
    };
  } catch (error) {
    console.error('Error getting trip plans by theme:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Get trip plan statistics
export const getTripPlanStats = async (userId) => {
  try {
    const tripPlansRef = collection(db, tripPlanModel.collection);
    
    // Get all trip plans (no filter) to include both owned and helper trips
    const querySnapshot = await getDocs(tripPlansRef);
    const allPlans = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.id && data.id !== '' && data.tripName && data.tripName.trim() !== '') {
        allPlans.push({
          id: doc.id,
          ...data
        });
      }
    });
    
    // Filter to show only trips where user is owner or helper
    const userPlans = allPlans.filter(plan => {
      // User is owner
      if (plan.userId === userId) return true;
      
      // User is helper
      if (plan.helpers && Array.isArray(plan.helpers)) {
        return plan.helpers.some(helper => helper.userId === userId);
      }
      
      return false;
    });
    
    const stats = {
      total: 0,
      draft: 0,
      active: 0,
      completed: 0,
      cancelled: 0
    };
    
    userPlans.forEach((plan) => {
      stats.total++;
      stats[plan.status] = (stats[plan.status] || 0) + 1;
    });
    
    return {
      success: true,
      data: stats
    };
  } catch (error) {
    console.error('Error getting trip plan stats:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Helper function to check if user can create trip plans
export const canUserCreateTripPlan = (userRole) => {
  const allowedRoles = ['customer', 'momo', 'girl'];
  return allowedRoles.includes(userRole);
};

// Helper function to check if user can view trip plan
export const canUserViewTripPlan = (tripPlan, userId, userRole) => {
  // Admin can view all
  if (userRole === 'admin') return true;
  
  // Owner can view their own
  if (tripPlan.userId === userId) return true;
  
  // Public trip plans can be viewed by anyone
  if (tripPlan.isPublic) return true;
  
  return false;
};

// Helper function to check if user can edit trip plan
export const canUserEditTripPlan = (tripPlan, userId, userRole) => {
  // Admin can edit all
  if (userRole === 'admin') return true;
  
  // Owner can edit their own
  if (tripPlan.userId === userId) return true;
  
  return false;
};

// Helper function to check if user can delete trip plan
export const canUserDeleteTripPlan = (tripPlan, userId, userRole) => {
  // Admin can delete all
  if (userRole === 'admin') return true;
  
  // Owner can delete their own
  if (tripPlan.userId === userId) return true;
  
  return false;
};

// ===== HELPER INVITATION SYSTEM =====

// Helper function to generate a unique token
const generateUniqueToken = () => {
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};

// Get invitation details by token
export const getInvitationDetailsByToken = async (token) => {
  try {
    if (!token) {
      return { success: false, error: 'Invitation token is required' };
    }

    console.log('=== DEBUG getInvitationDetailsByToken ===');
    console.log('Searching for token:', token);

    const tripPlansRef = collection(db, 'tripPlans');
    const querySnapshot = await getDocs(tripPlansRef);
    let foundTripPlan = null;
    let foundInvitation = null;

    console.log('Total trip plans found:', querySnapshot.size);

    querySnapshot.forEach(doc => {
      const tripPlanData = doc.data();
      console.log(`Trip Plan ${doc.id}:`, {
        id: doc.id,
        tripName: tripPlanData.tripName,
        createdBy: tripPlanData.createdBy,
        invitations: tripPlanData.invitations ? tripPlanData.invitations.length : 0
      });

      if (tripPlanData.invitations && Array.isArray(tripPlanData.invitations)) {
        console.log(`Invitations in ${doc.id}:`, tripPlanData.invitations.map(inv => ({
          email: inv.email,
          token: inv.token,
          status: inv.status
        })));

        const invitation = tripPlanData.invitations.find(inv => inv.token === token);
        if (invitation) {
          console.log('Found matching invitation:', invitation);
          console.log('Document ID from Firestore:', doc.id);
          console.log('Trip Plan Data from Firestore:', tripPlanData);
          foundTripPlan = { id: doc.id, ...tripPlanData };
          foundInvitation = invitation;
          return; // Break forEach
        }
      }
    });

    console.log('Final result:', {
      foundTripPlan: foundTripPlan ? foundTripPlan.id : null,
      foundInvitation: foundInvitation ? foundInvitation.email : null
    });
    console.log('==========================================');

    if (foundTripPlan && foundInvitation) {
      return { success: true, tripPlan: foundTripPlan, invitation: foundInvitation };
    } else {
      return { success: false, error: 'Invitation not found or invalid' };
    }

  } catch (error) {
    console.error('Error getting invitation details by token:', error);
    return { success: false, error: error.message };
  }
};

// Invite helper to trip plan
export const inviteHelper = async (tripPlanId, email) => {
  try {
    if (!tripPlanId || !email) {
      throw new Error('Trip plan ID and email are required');
    }

    // Check if email is already invited
    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    const tripPlanSnap = await getDoc(tripPlanRef);
    
    if (!tripPlanSnap.exists()) {
      throw new Error('Trip plan not found');
    }

    const tripPlanData = tripPlanSnap.data();
    
    // Check if email is already in helpers or invitations
    const existingHelper = tripPlanData.helpers?.find(h => h.email === email);
    const existingInvitation = tripPlanData.invitations?.find(i => i.email === email);
    
    if (existingHelper || existingInvitation) {
      throw new Error('อีเมลนี้ได้รับคำเชิญแล้ว');
    }

    const token = generateUniqueToken();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // Expires in 7 days

    const newInvitation = {
      email,
      token,
      role: 'helper',
      status: 'pending',
      invitedAt: new Date().toISOString(),
      expiresAt: expiresAt,
    };

    await updateDoc(tripPlanRef, {
      invitations: arrayUnion(newInvitation)
    });

    // TODO: Send email notification here
    console.log('Invitation sent to:', email, 'Token:', token);

    return { success: true, message: 'Invitation sent successfully!', token: token };
  } catch (error) {
    console.error('Error inviting helper:', error);
    throw error;
  }
};

// Accept invitation
export const acceptInvitation = async (invitationToken, userId, userEmail) => {
  try {
    if (!invitationToken || !userId || !userEmail) {
      throw new Error('Invitation token, user ID, and user email are required');
    }

    console.log('=== DEBUG acceptInvitation ===');
    console.log('Token:', invitationToken);
    console.log('User ID:', userId);
    console.log('User Email:', userEmail);

    // Find trip plan by invitation token
    const tripPlansRef = collection(db, 'tripPlans');
    const querySnapshot = await getDocs(tripPlansRef);
    let foundTripPlan = null;
    let foundInvitation = null;

    querySnapshot.forEach(doc => {
      const tripPlanData = doc.data();
      if (tripPlanData.invitations && Array.isArray(tripPlanData.invitations)) {
        const invitation = tripPlanData.invitations.find(inv => inv.token === invitationToken);
        if (invitation) {
          foundTripPlan = { id: doc.id, ...tripPlanData };
          foundInvitation = invitation;
          return; // Break forEach
        }
      }
    });

    if (!foundTripPlan || !foundInvitation) {
      throw new Error('Invitation not found or invalid');
    }

    console.log('Found trip plan:', foundTripPlan.id);
    console.log('Found invitation:', foundInvitation);

    if (foundInvitation.email !== userEmail) {
      throw new Error('Email does not match invitation');
    }

    if (foundInvitation.status !== 'pending') {
      throw new Error('Invitation is not pending');
    }

    if (new Date(foundInvitation.expiresAt) < new Date()) {
      throw new Error('Invitation has expired');
    }

    const tripPlanRef = doc(db, 'tripPlans', foundTripPlan.id);
    let invitations = foundTripPlan.invitations || [];
    let helpers = foundTripPlan.helpers || [];

    const invitationIndex = invitations.findIndex(inv => inv.token === invitationToken);

    if (invitationIndex === -1) {
      throw new Error('Invitation not found in trip plan');
    }

    // Update invitation status
    invitations[invitationIndex] = {
      ...foundInvitation,
      status: 'accepted',
      acceptedAt: new Date().toISOString(),
      acceptedBy: userId,
    };

    // Add helper to the trip plan
    const newHelper = {
      userId: userId,
      email: userEmail,
      role: foundInvitation.role, // 'helper'
      joinedAt: new Date().toISOString(),
    };

    // Ensure no duplicate helpers
    if (!helpers.some(h => h.userId === userId)) {
      helpers.push(newHelper);
    }

    await updateDoc(tripPlanRef, {
      invitations: invitations,
      helpers: helpers,
    });

    return { success: true, message: 'Invitation accepted successfully!' };

  } catch (error) {
    console.error('Error accepting invitation:', error);
    return { success: false, error: error.message };
  }
};

// Get trip plan helpers
export const getTripPlanHelpers = async (tripPlanId) => {
  try {
    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    const tripPlanSnap = await getDoc(tripPlanRef);
    
    if (!tripPlanSnap.exists()) {
      throw new Error('Trip plan not found');
    }

    const tripPlanData = tripPlanSnap.data();
    
    return {
      success: true,
      helpers: tripPlanData.helpers || [],
      invitations: tripPlanData.invitations || []
    };
  } catch (error) {
    console.error('Error getting trip plan helpers:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Remove helper from trip plan
export const removeHelper = async (tripPlanId, helperEmail) => {
  try {
    if (!tripPlanId || !helperEmail) {
      throw new Error('Trip plan ID and helper email are required');
    }

    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    const tripPlanSnap = await getDoc(tripPlanRef);
    
    if (!tripPlanSnap.exists()) {
      throw new Error('Trip plan not found');
    }

    const tripPlanData = tripPlanSnap.data();
    const updatedHelpers = (tripPlanData.helpers || []).filter(h => h.email !== helperEmail);
    
    await updateDoc(tripPlanRef, {
      helpers: updatedHelpers,
      lastUpdated: new Date().toISOString()
    });

    return {
      success: true
    };
  } catch (error) {
    console.error('Error removing helper:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Check if user is helper for trip plan
export const isUserHelper = (tripPlan, userId) => {
  if (!tripPlan.helpers) return false;
  return tripPlan.helpers.some(helper => helper.userId === userId && helper.status === 'accepted');
};

// Check if user can edit trip plan (including helpers)
export const canUserEditTripPlanWithHelpers = (tripPlan, userId, userRole) => {
  // Admin can edit all
  if (userRole === 'admin') return true;
  
  // Owner can edit their own
  if (tripPlan.userId === userId) return true;
  
  // Helper can edit if they have permission
  if (isUserHelper(tripPlan, userId)) return true;
  
  return false;
};

// Remove invitation by email
export const removeInvitation = async (tripPlanId, email) => {
  try {
    const tripPlanRef = doc(db, tripPlanModel.collection, tripPlanId);
    const tripPlanSnap = await getDoc(tripPlanRef);

    if (!tripPlanSnap.exists()) {
      throw new Error('Trip plan not found');
    }

    const tripPlanData = tripPlanSnap.data();
    const invitations = tripPlanData.invitations || [];
    
    const updatedInvitations = invitations.filter(invitation => invitation.email !== email);
    
    await updateDoc(tripPlanRef, {
      invitations: updatedInvitations,
      lastUpdated: new Date().toISOString()
    });

    return { success: true };
  } catch (error) {
    console.error('Error removing invitation:', error);
    throw error;
  }
};
